import { useEffect, useState } from 'react';

import type { BatchResponse, BatchStatus } from '@cartwhl/common/api/getBatch';
import getCharacterGenerationsByBatch from '@cartwhl/common/api/getCharacterGenerationsByBatch';

import { isCharacterFbxDownloadReady } from './characterJobStatus';

/** Matches ModelServing `character_thumbnail_renderer.DEFAULT_BKG_HEX`. */
export const CHARACTER_THUMBNAIL_BACKGROUND = '#8eb1ba';

export const CHARACTER_THUMBNAIL_BACKGROUND_CLASS = 'bg-[#8eb1ba]';

/**
 * T-pose image-gen prompt background — `geminiService.ts`
 * `buildCharacterTposePromptFromText` / `FromImage`: "Background: light gray (#ABABAB)".
 */
export const CHARACTER_IMAGE_GENERATION_BACKGROUND = '#ABABAB';

/** Letterbox while a tile is in-flight but the hero image has not been sampled yet. */
export function resolveCharacterProcessingLetterboxFallback(
  uploadStatus: string | undefined | null,
): string {
  if (
    uploadStatus === 'AUTORIGGING_IN_PROGRESS' ||
    uploadStatus === 'RETARGETING_IN_PROGRESS'
  ) {
    return CHARACTER_THUMBNAIL_BACKGROUND;
  }
  return CHARACTER_IMAGE_GENERATION_BACKGROUND;
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Inset from the top-right corner into the preview PNG background (avoids edge artifacts). */
export const PREVIEW_LETTERBOX_SAMPLE_INSET_PX = 20;

/**
 * Samples background color from a loaded, CORS-safe preview image — 20px left and
 * down from the top-right corner (clamped to image bounds).
 */
export function sampleImageTopRightPixelColor(
  img: HTMLImageElement,
  insetPx: number = PREVIEW_LETTERBOX_SAMPLE_INSET_PX,
): string | null {
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (!width || !height) return null;

  const sampleX = Math.min(width - 1, Math.max(0, width - 1 - insetPx));
  const sampleY = Math.min(height - 1, Math.max(0, insetPx));

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(img, sampleX, sampleY, 1, 1, 0, 0, 1, 1);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    return rgbToHex(data[0]!, data[1]!, data[2]!);
  } catch {
    return null;
  }
}

/** First successful character thumbnail; batch thumbnails may point to failed jobs. */
export async function resolveCharacterBatchHistoryPreviewURL(
  batchID: string,
  apiKey: string,
): Promise<string | undefined> {
  let nextToken: string | undefined;

  do {
    const { data, error } = await getCharacterGenerationsByBatch(
      batchID,
      apiKey,
      { limit: 100, nextToken },
    );
    if (error || !data) {
      break;
    }

    for (const item of data.items) {
      if (!isCharacterFbxDownloadReady(item.uploadStatus)) continue;
      const url = item.thumbnailURL?.trim();
      if (url) {
        return url;
      }
    }

    nextToken = data.nextToken;
  } while (nextToken);

  return undefined;
}

export function characterBatchNeedsHistoryPreviewFallback(
  job: Pick<BatchResponse, 'status'>,
): boolean {
  return job.status === 'COMPLETED';
}

export function useCharacterBatchHistoryPreviewURL(
  batchID: string | undefined,
  status: BatchStatus | undefined,
  apiKey: string | undefined,
): string | undefined {
  const [resolvedPreview, setResolvedPreview] = useState<{
    batchID: string;
    apiKey: string;
    url: string;
  }>();

  useEffect(() => {
    if (!batchID || !apiKey || status !== 'COMPLETED') return;

    let cancelled = false;
    void (async () => {
      try {
        const url = await resolveCharacterBatchHistoryPreviewURL(batchID, apiKey);
        if (!cancelled && url) {
          setResolvedPreview({ batchID, apiKey, url });
        }
      } catch {
        // A missing preview leaves the same placeholder used by recent jobs.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [batchID, status, apiKey]);

  return resolvedPreview &&
    status === 'COMPLETED' &&
    resolvedPreview.batchID === batchID &&
    resolvedPreview.apiKey === apiKey
    ? resolvedPreview.url
    : undefined;
}
